import {
  StyledFullScreenContainer,
  StyledModalOverlay,
  StyledModalWrapper,
} from "@/mds/components/overlays/modal/modal.styles";
import { useMDSHotKey } from "@/mds/domains/hotkeys/use-mds-hot-key";
import { useMDSModalGlobalHotkeysBlocker } from "@/mds/components/overlays/modal/modal-global-hotkeys-blocker";
import { useMDSModalRenderLock } from "@/mds/components/overlays/modal/modal-render-lock";
import { IMDSModalProps, MDSModalZIndex } from "@/mds/components/overlays/modal/modal.types";
import { MDSFloatingPortalRootProvider } from "@/mds/components/floating/floating-portal-root";
import type { ModalRenderProps } from "react-aria-components";
import { Modal } from "react-aria-components";
import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";

declare const __GLOBAL_ENV__: { IS_DESKTOP?: string } | undefined;

type GlobalWithInjectedEnv = typeof globalThis & {
  __GLOBAL_ENV__?: { IS_DESKTOP?: string };
};

const getInjectedGlobalEnv = () =>
  (globalThis as GlobalWithInjectedEnv).__GLOBAL_ENV__ ??
  (typeof __GLOBAL_ENV__ === "undefined" ? undefined : __GLOBAL_ENV__);

const isDesktopApp = (): boolean => getInjectedGlobalEnv()?.IS_DESKTOP === "true";

const getNavigatorPlatformText = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const navigatorWithUserAgentData = window.navigator as Navigator & {
    userAgentData?: { platform?: string };
  };

  return [
    navigatorWithUserAgentData.userAgentData?.platform,
    window.navigator.platform,
    window.navigator.userAgent,
  ]
    .filter(Boolean)
    .join(" ");
};

const isMacOs = (): boolean => /macintosh|mac os|macintel|mac/i.test(getNavigatorPlatformText());

const isIos = (): boolean => {
  const platformText = getNavigatorPlatformText();

  return /iphone|ipad|ipod|ios/i.test(platformText);
};

const getCurrentPlatformModalCloseHotkey = () => {
  const currentPlatformIsMacOs = isMacOs();
  const isApplePlatform = currentPlatformIsMacOs || isIos();

  if (!isDesktopApp()) {
    return currentPlatformIsMacOs ? "Control+W" : "Alt+W";
  }

  return isApplePlatform ? "Mod+W" : "Control+W";
};

type ModalCloseHotkey = ReturnType<typeof getCurrentPlatformModalCloseHotkey>;
type KeyboardEventLike = Pick<
  KeyboardEvent,
  "altKey" | "code" | "ctrlKey" | "key" | "metaKey" | "shiftKey"
>;

const isWKeyEvent = (event: KeyboardEventLike): boolean => {
  return event.code === "KeyW" || event.key.toLowerCase() === "w";
};

const isModalCloseHotkeyEvent = (
  event: KeyboardEventLike,
  modalCloseHotkey: ModalCloseHotkey
): boolean => {
  if (!isWKeyEvent(event) || event.shiftKey) {
    return false;
  }

  switch (modalCloseHotkey) {
    case "Mod+W":
      return event.metaKey && !event.ctrlKey && !event.altKey;
    case "Control+W":
      return event.ctrlKey && !event.metaKey && !event.altKey;
    case "Alt+W":
      return event.altKey && !event.ctrlKey && !event.metaKey;
  }
};

export const MDSModal = ({
  zIndex = MDSModalZIndex.Default,
  handleClose,
  onOpenChange,
  isDismissable = true,
  ...props
}: IMDSModalProps) => {
  const modalId = useId();
  const modalRenderLock = useMDSModalRenderLock();
  const modalRenderLockRef = useRef(modalRenderLock);
  const [floatingPortalRoot, setFloatingPortalRoot] = useState<HTMLDivElement | null>(null);
  const lastRequestedResetOwnerIdRef = useRef<string | null>(null);
  modalRenderLockRef.current = modalRenderLock;

  const requestedOpen = props.isOpen === true;
  const renderLockClaim = requestedOpen
    ? modalRenderLock.claimMDSModalRenderSlot(modalId)
    : { canRender: true, ownerId: null };
  const isOpen = requestedOpen && renderLockClaim.canRender;

  const canDismissWithHotkeys = isOpen && isDismissable && !!handleClose;
  const modalCloseHotkey = getCurrentPlatformModalCloseHotkey();
  const handleDismissHotkey = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      handleClose?.();
    },
    [handleClose]
  );
  const dismissHotkeyOptions = {
    conflictBehavior: "allow" as const,
    enabled: canDismissWithHotkeys,
    ignoreInputs: false,
    preventDefault: false,
    stopPropagation: true,
  };

  useMDSHotKey("Escape", handleDismissHotkey, dismissHotkeyOptions);
  useMDSHotKey(modalCloseHotkey, handleDismissHotkey, dismissHotkeyOptions);

  useLayoutEffect(() => {
    if (!requestedOpen || renderLockClaim.canRender || !renderLockClaim.ownerId) {
      lastRequestedResetOwnerIdRef.current = null;
      return;
    }

    if (lastRequestedResetOwnerIdRef.current === renderLockClaim.ownerId) {
      return;
    }

    lastRequestedResetOwnerIdRef.current = renderLockClaim.ownerId;
    modalRenderLockRef.current.requestMDSModalRenderLockReset();
  }, [renderLockClaim.canRender, renderLockClaim.ownerId, requestedOpen]);

  useLayoutEffect(() => {
    if (!requestedOpen || !renderLockClaim.canRender) {
      return;
    }

    return () => modalRenderLockRef.current.releaseMDSModalRenderSlot(modalId);
  }, [modalId, renderLockClaim.canRender, requestedOpen]);

  useLayoutEffect(() => {
    if (!canDismissWithHotkeys) {
      return;
    }

    const handleKeyDownCapture = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key !== "Escape" && !isModalCloseHotkeyEvent(event, modalCloseHotkey)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handleClose?.();
    };

    document.addEventListener("keydown", handleKeyDownCapture, true);
    return () => document.removeEventListener("keydown", handleKeyDownCapture, true);
  }, [canDismissWithHotkeys, handleClose, modalCloseHotkey]);

  const handleOpenChange = (open: boolean) => {
    if (!open && isDismissable) {
      handleClose?.();
      onOpenChange?.(open);
    } else if (open) {
      onOpenChange?.(open);
    }
  };

  const handleOverlayClick = useCallback(() => {
    // Only close the modal if clicking directly on the overlay background,
    // not on floating elements like dropdowns that are rendered in portals
    if (isDismissable) handleClose?.();
  }, [isDismissable, handleClose]);

  const handleModalClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Eat clicks so the overlay doesn't capture the event.
    e.stopPropagation();
    return true;
  }, []);

  const forwardedProps = { isDismissable, onOpenChange: handleOpenChange, ...props, isOpen };
  const { children: modalChildren, ...overlayProps } = forwardedProps;

  return (
    <StyledModalOverlay $zIndex={zIndex} {...overlayProps} isDismissable={false}>
      {({ isExiting }: ModalRenderProps) => (
        <>
          <MDSModalVisibleEffects isVisible={isOpen || isExiting} />
          <StyledFullScreenContainer onClick={handleOverlayClick}>
            <StyledModalWrapper onClick={handleModalClick}>
              <MDSFloatingPortalRootProvider portalRoot={floatingPortalRoot}>
                <Modal ref={setFloatingPortalRoot}>{modalChildren}</Modal>
              </MDSFloatingPortalRootProvider>
            </StyledModalWrapper>
          </StyledFullScreenContainer>
        </>
      )}
    </StyledModalOverlay>
  );
};

const MDSModalVisibleEffects = ({ isVisible }: { isVisible: boolean }) => {
  const useModalGlobalHotkeysBlocker = useMDSModalGlobalHotkeysBlocker();

  useModalGlobalHotkeysBlocker(isVisible);

  return null;
};
